import type MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { action, makeObservable, observable, runInAction } from "mobx";
import type {
    TextItem,
    UserContext,
    UserContextListField,
    UserContextProfile,
} from "models/ClientBrain/UserContext";
import type { UpdateUserContextPayload } from "services/ClientBrainService";
import { BaseStore } from "stores/BaseStore";

export type SaveUserContext = (
    userId: string,
    payload: UpdateUserContextPayload,
) => Promise<UserContextProfile>;

function cloneContext(context: UserContext): UserContext {
    return JSON.parse(JSON.stringify(context));
}

function newId(): string {
    return typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

function collapseItems(items: TextItem[]): TextItem[] {
    const nonemptyItems = items.filter((item) => item.text.trim().length > 0);
    if (nonemptyItems.length === 0) return [];

    return [
        {
            id: nonemptyItems[0].id || newId(),
            text: nonemptyItems.map((item) => item.text.trim()).join("\n\n"),
        },
    ];
}

/**
 * Per-user Client Brain context store. One instance is created per user profile by the
 * singleton ClientBrainStore (mirrors Signals' DashboardStore). Holds the user's identity +
 * context, an editable draft, and persists via the shared ClientBrainService.
 */
export class UserContextStore extends BaseStore {
    private readonly saveContext: SaveUserContext;
    private readonly messageStore: MessageStore;

    readonly userId: string;
    @observable firstName: string;
    @observable lastName: string;
    @observable email: string;
    @observable context: UserContext;

    @observable isEditing = false;
    @observable isSaving = false;
    @observable draft: UserContext;

    constructor(
        profile: UserContextProfile,
        messageStore: MessageStore,
        saveContext: SaveUserContext,
    ) {
        super("UserContextStore");
        makeObservable(this);

        this.userId = profile.userId;
        this.firstName = profile.firstName;
        this.lastName = profile.lastName;
        this.email = profile.email;
        this.context = profile.context;
        this.draft = cloneContext(profile.context);

        this.saveContext = saveContext;
        this.messageStore = messageStore;
    }

    get displayName(): string {
        return `${this.firstName} ${this.lastName}`.trim();
    }

    @action
    startEdit() {
        this.draft = cloneContext(this.context);
        this.isEditing = true;
    }

    @action
    cancelEdit() {
        this.draft = cloneContext(this.context);
        this.isEditing = false;
    }

    @action
    setRole(value: string) {
        this.draft = { ...this.draft, role: value };
    }

    @action
    setListFieldText(field: UserContextListField, text: string) {
        const firstNonempty =
            this.draft[field].find((item) => item.text.trim().length > 0) ??
            this.context[field].find((item) => item.text.trim().length > 0);
        this.draft = {
            ...this.draft,
            [field]:
                text.trim().length === 0
                    ? []
                    : [
                          {
                              id: firstNonempty?.id ?? newId(),
                              text,
                          },
                      ],
        };
    }

    @action
    async save() {
        this.isSaving = true;
        try {
            const profile = await this.saveContext(this.userId, {
                role: this.draft.role,
                goals: collapseItems(this.draft.goals),
                responsibilities: collapseItems(this.draft.responsibilities),
                successMetrics: collapseItems(this.draft.successMetrics),
                currentConcerns: collapseItems(this.draft.currentConcerns),
            });
            runInAction(() => {
                this.context = profile.context;
                this.draft = cloneContext(profile.context);
                this.isEditing = false;
            });
            this.messageStore.logMessage("User context saved", "success");
        } catch (err: unknown) {
            const msg =
                err instanceof Error
                    ? err.message
                    : "Failed to save user context";
            this.messageStore.logError(msg);
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }
}

export default UserContextStore;
